import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAccount, useConnect, useSendTransaction, useSwitchChain, useWriteContract } from "wagmi";
import { parseUnits } from "viem";
import { injectedConnector } from "../walletConfig";

const manageAbi = [
  {
    type: "function",
    name: "setTaxConfig",
    stateMutability: "nonpayable",
    inputs: [
      { name: "taxBps_", type: "uint16" },
      { name: "taxWallets_", type: "address[]" },
      { name: "taxWalletBps_", type: "uint16[]" },
    ],
    outputs: [],
  },
  {
    type: "function",
    name: "setSwapbackConfig",
    stateMutability: "nonpayable",
    inputs: [
      { name: "enabled", type: "bool" },
      { name: "router", type: "address" },
      { name: "tokenOut", type: "address" },
      { name: "threshold", type: "uint256" },
    ],
    outputs: [],
  },
];

const BillingPage = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const apiKey = process.env.REACT_APP_API_KEY;
  const feeReceiver = process.env.REACT_APP_FEE_RECEIVER_ADDRESS;

  const [assetAddress, setAssetAddress] = useState("");
  const [billingInfo, setBillingInfo] = useState(null);
  const [txHash, setTxHash] = useState("");
  const [payChain, setPayChain] = useState("bsc");
  const [statusMessage, setStatusMessage] = useState("");
  const [statusTone, setStatusTone] = useState("info");
  const [isLoading, setIsLoading] = useState(false);

  const [taxRate, setTaxRate] = useState("");
  const [taxMarketingWallet, setTaxMarketingWallet] = useState("");
  const [taxMarketingPct, setTaxMarketingPct] = useState("");
  const [taxDevWallet, setTaxDevWallet] = useState("");
  const [taxDevPct, setTaxDevPct] = useState("");
  const [taxLpWallet, setTaxLpWallet] = useState("");
  const [taxLpPct, setTaxLpPct] = useState("");
  const [swapbackEnabled, setSwapbackEnabled] = useState(false);
  const [swapbackThreshold, setSwapbackThreshold] = useState("");
  const [swapbackRouter, setSwapbackRouter] = useState("");
  const [swapbackTokenOut, setSwapbackTokenOut] = useState("");

  const { address, isConnected, chainId } = useAccount();
  const { connect } = useConnect();
  const { switchChainAsync } = useSwitchChain();
  const { writeContractAsync } = useWriteContract();
  const { sendTransactionAsync } = useSendTransaction();

  const isBesc = chainId === 2372;
  const isBsc = chainId === 56;
  const isEth = chainId === 1;
  const isManager = useMemo(() => {
    if (!address || !billingInfo?.management_wallet) return false;
    return address.toLowerCase() === billingInfo.management_wallet.toLowerCase();
  }, [address, billingInfo]);

  const showStatus = (message, tone = "info") => {
    setStatusMessage(message);
    setStatusTone(tone);
  };

  const fetchBilling = async () => {
    if (!assetAddress || !backendUrl || !apiKey) {
      showStatus("Enter a wrapped asset address.", "error");
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetch(`${backendUrl}/billing/asset/${assetAddress}`, {
        headers: { "x-api-key": apiKey },
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Unable to load billing details");
      }
      const result = await response.json();
      setBillingInfo({ ...result.listing, quote: result.quote });
      showStatus("Billing details loaded.", "success");
    } catch (error) {
      console.error(error);
      showStatus("Unable to load billing details.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePayNow = async () => {
    if (!billingInfo) return;
    if (!feeReceiver) {
      showStatus("Fee payment wallet is not configured. Contact support.", "error");
      return;
    }
    if (!isConnected) {
      showStatus("Connect the billing wallet to pay.", "error");
      return;
    }
    if ((payChain === "bsc" && !isBsc) || (payChain === "eth" && !isEth)) {
      try {
        await switchChainAsync({ chainId: payChain === "bsc" ? 56 : 1 });
      } catch (error) {
        showStatus("Switch to the selected payment chain to pay.", "error");
        return;
      }
    }

    try {
      setIsLoading(true);
      const quote = billingInfo?.quote?.[payChain];
      if (!quote) {
        showStatus("Unable to calculate the required payment. Contact support.", "error");
        setIsLoading(false);
        return;
      }
      const hash = await sendTransactionAsync({
        to: feeReceiver,
        value: BigInt(quote),
      });
      setTxHash(hash);
      showStatus("Payment submitted. Submit the transaction hash to finalize.", "success");
    } catch (error) {
      console.error(error);
      showStatus("Payment failed. Please try again.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitPayment = async () => {
    if (!billingInfo || !txHash) {
      showStatus("Enter the payment transaction hash.", "error");
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetch(`${backendUrl}/billing/asset/${billingInfo.besc_wrapped_address}/pay`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": apiKey,
        },
        body: JSON.stringify({
          txHash,
          payerAddress: address,
          chain: payChain,
        }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Payment verification failed");
      }
      const result = await response.json();
      setBillingInfo((prev) => ({ ...prev, ...result.listing }));
      showStatus("Payment verified and applied.", "success");
    } catch (error) {
      console.error(error);
      showStatus("Payment verification failed. Contact support.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const dueDate = useMemo(() => {
    if (!billingInfo?.next_fee_due_at) return "—";
    const date = new Date(billingInfo.next_fee_due_at);
    return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
  }, [billingInfo]);

  const formattedQuote = useMemo(() => {
    const quote = billingInfo?.quote?.[payChain];
    if (!quote) return null;
    const value = Number(quote) / 1e18;
    if (!Number.isFinite(value)) return null;
    return value.toFixed(6);
  }, [billingInfo, payChain]);

  const tokenDecimals = useMemo(() => {
    const raw = Number(billingInfo?.decimals);
    return Number.isFinite(raw) ? raw : 18;
  }, [billingInfo]);

  const handleUpdateTax = async () => {
    if (!billingInfo?.besc_wrapped_address) return;
    const totalPct =
      Number(taxMarketingPct || 0) + Number(taxDevPct || 0) + Number(taxLpPct || 0);
    if (!taxRate || Number(taxRate) <= 0) {
      showStatus("Enter a total tax rate.", "error");
      return;
    }
    if (Math.abs(totalPct - 100) > 0.01) {
      showStatus("Tax shares must total 100%.", "error");
      return;
    }
    try {
      setIsLoading(true);
      if (!isBesc) {
        await switchChainAsync({ chainId: 2372 });
      }
      const taxBps = Math.round(Number(taxRate) * 100);
      const wallets = [taxMarketingWallet, taxDevWallet, taxLpWallet];
      const bps = [
        Math.round(Number(taxMarketingPct) * 100),
        Math.round(Number(taxDevPct) * 100),
        Math.round(Number(taxLpPct) * 100),
      ];
      await writeContractAsync({
        address: billingInfo.besc_wrapped_address,
        abi: manageAbi,
        functionName: "setTaxConfig",
        args: [taxBps, wallets, bps],
      });
      showStatus("Tax configuration updated.", "success");
    } catch (error) {
      console.error(error);
      showStatus("Unable to update tax config.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateSwapback = async () => {
    if (!billingInfo?.besc_wrapped_address) return;
    try {
      setIsLoading(true);
      if (!isBesc) {
        await switchChainAsync({ chainId: 2372 });
      }
      await writeContractAsync({
        address: billingInfo.besc_wrapped_address,
        abi: manageAbi,
        functionName: "setSwapbackConfig",
        args: [
          swapbackEnabled,
          swapbackRouter,
          swapbackTokenOut,
          parseUnits(swapbackThreshold || "0", tokenDecimals),
        ],
      });
      showStatus("Swapback configuration updated.", "success");
    } catch (error) {
      console.error(error);
      showStatus("Unable to update swapback config.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="billing-page">
      <div className="billing-page__header">
        <div>
          <p className="billing-page__eyebrow">Billing &amp; Manage</p>
          <h1>Billing &amp; Manage</h1>
          <p>Pay your monthly bridge maintenance fee and manage tax and swapback settings.</p>
        </div>
        <Link to="/" className="secondary-button ghost">
          Back to bridge
        </Link>
      </div>

      <div className="billing-page__panel">
        <div className="billing-page__input">
          <label>
            Wrapped asset address
            <input
              type="text"
              value={assetAddress}
              onChange={(event) => setAssetAddress(event.target.value)}
              placeholder="0x..."
            />
          </label>
          <button className="primary-button" onClick={fetchBilling} disabled={isLoading}>
            Load billing &amp; manage
          </button>
        </div>

        {statusMessage && (
          <div className={`status-banner status-banner--${statusTone}`}>
            <span className="status-banner__message">{statusMessage}</span>
          </div>
        )}

        {billingInfo && (
          <div className="billing-page__details">
            <div>
              <p>
                <strong>Token</strong> {billingInfo.token_name} ({billingInfo.symbol})
              </p>
              <p>
                <strong>Fee</strong> ${billingInfo.monthly_fee_amount || 250} {billingInfo.monthly_fee_currency || "USD"}
              </p>
              <p>
                <strong>Next due</strong> {dueDate}
              </p>
            </div>
            <div>
              <p>
                <strong>Billing wallet</strong> {billingInfo.billing_wallet || "—"}
              </p>
              <p>
                <strong>Status</strong> {billingInfo.fee_status || "unpaid"}
              </p>
            </div>
          </div>
        )}
      </div>

      {billingInfo && (
        <div className="billing-page__panel">
          <div className="billing-page__wallet">
            {isConnected ? (
              <div className="billing-page__wallet-info">
                <span>Connected wallet</span>
                <strong>{address}</strong>
              </div>
            ) : (
              <button className="secondary-button" onClick={() => connect({ connector: injectedConnector })}>
                Connect billing wallet
              </button>
            )}
          </div>

          <div className="billing-page__actions">
            <div className="billing-page__chain">
              <label>
                Pay on
                <select value={payChain} onChange={(event) => setPayChain(event.target.value)}>
                  <option value="bsc">BNB Smart Chain</option>
                  <option value="eth">Ethereum</option>
                </select>
              </label>
              {formattedQuote && (
                <p>
                  Estimated amount: {formattedQuote} {payChain === "bsc" ? "BNB" : "ETH"}
                </p>
              )}
            </div>
            <button className="primary-button" onClick={handlePayNow} disabled={isLoading || !isConnected}>
              Pay now
            </button>
            <div className="billing-page__tx">
              <label>
                Payment tx hash
                <input
                  type="text"
                  value={txHash}
                  onChange={(event) => setTxHash(event.target.value)}
                  placeholder="0x..."
                />
              </label>
              <button className="secondary-button" onClick={handleSubmitPayment} disabled={isLoading}>
                Submit payment
              </button>
            </div>
          </div>
          <p className="billing-page__note">
            Send the native coin on the selected chain to the fee wallet. If needed, pay manually and submit the hash.
          </p>
        </div>
      )}

      {billingInfo && isManager && (
        <div className="billing-page__panel">
          <div className="billing-page__header">
            <div>
              <p className="billing-page__eyebrow">Manage token</p>
              <h2>Tax & Swapback Settings</h2>
              <p>Update tax wallets and swapback thresholds at any time.</p>
            </div>
          </div>
          <div className="form-grid form-grid--tax">
            <label>
              Total tax rate (%)
              <input value={taxRate} onChange={(e) => setTaxRate(e.target.value)} type="number" step="0.01" />
            </label>
            <label>
              Marketing wallet
              <input value={taxMarketingWallet} onChange={(e) => setTaxMarketingWallet(e.target.value)} type="text" />
            </label>
            <label>
              Marketing share (%)
              <input value={taxMarketingPct} onChange={(e) => setTaxMarketingPct(e.target.value)} type="number" step="0.01" />
            </label>
            <label>
              Dev wallet
              <input value={taxDevWallet} onChange={(e) => setTaxDevWallet(e.target.value)} type="text" />
            </label>
            <label>
              Dev share (%)
              <input value={taxDevPct} onChange={(e) => setTaxDevPct(e.target.value)} type="number" step="0.01" />
            </label>
            <label>
              LP wallet
              <input value={taxLpWallet} onChange={(e) => setTaxLpWallet(e.target.value)} type="text" />
            </label>
            <label>
              LP share (%)
              <input value={taxLpPct} onChange={(e) => setTaxLpPct(e.target.value)} type="number" step="0.01" />
            </label>
            <div className="form-note">
              Shares must total 100%. The bridge minter wallet is tax‑exempt.
            </div>
            <button className="secondary-button" onClick={handleUpdateTax} disabled={isLoading}>
              Update tax config
            </button>
          </div>

          <div className="form-grid form-grid--tax">
            <label>
              Swapback enabled
              <select value={swapbackEnabled ? "yes" : "no"} onChange={(e) => setSwapbackEnabled(e.target.value === "yes")}>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </label>
            <label>
              Swapback threshold (token amount)
              <input value={swapbackThreshold} onChange={(e) => setSwapbackThreshold(e.target.value)} type="number" step="0.0001" />
            </label>
            <label>
              Router address
              <input value={swapbackRouter} onChange={(e) => setSwapbackRouter(e.target.value)} type="text" />
            </label>
            <label>
              Output token (WNATIVE)
              <input value={swapbackTokenOut} onChange={(e) => setSwapbackTokenOut(e.target.value)} type="text" />
            </label>
            <button className="secondary-button" onClick={handleUpdateSwapback} disabled={isLoading}>
              Update swapback
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default BillingPage;
